/**
 * FormFlo — public marketing landing page.
 *
 * Sections: nav, hero, feature grid, how-it-works, pricing (annual, no taxes,
 * driven by /api/public/pricing), footer with Rishmathi credit + legal links.
 *
 * CTAs: "Get started" (signup) and "Request a demo" (modal).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RequestDemoDialog } from "@/components/app/RequestDemoDialog";
import {
    FilesIcon, UsersThree, GitBranch, Sparkle,
    ArrowRight, Stack, LockKey, ChartBar, Palette, CheckCircle, Pulse, ShareNetwork,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
    { icon: FilesIcon, title: "Metadata-driven forms", copy: "17 field types — text, number, decimal, phone, email, date, datetime, dropdown, multi-select, checkbox, radio, toggle, file, image, lookup, plus a nested table grid. Validation, conditional visibility, sections.", tag: "Builder" },
    { icon: ShareNetwork, title: "Linked forms & lookups", copy: "Reference records across forms — point a Lookup field at another form, search live, store the relation. Build invoices linked to customers, leads linked to accounts, tickets linked to assets.", tag: "Relations" },
    { icon: GitBranch, title: "Approval workflows", copy: "Multi-step routing to users, groups or roles. Hide / mark read-only / require fields per step. Hide entire sections. 1-click magic-link approvals via email.", tag: "Workflow" },
    { icon: ChartBar, title: "Reports & variants", copy: "Save named reports, assign to teams via groups/roles, schedule digests. Bar, line, pie. Export to PDF or Excel — including nested table data on its own sheet.", tag: "Insights" },
    { icon: Sparkle, title: "Group & role defaults", copy: "Pre-fill or lock fields per group / per role. Org-wide group defaults plus per-app overrides. Auto-generate sequential IDs (PO-001, PO-002…). Dependent dropdowns where the child options react to the parent value.", tag: "Defaults" },
    { icon: Stack, title: "Layout modes", copy: "Render every form as a single page, a stepper or tabs — same metadata. Tabs and stepper auto-open in a full-page editor instead of a dialog.", tag: "UX" },
    { icon: Palette, title: "Themes", copy: "Cohesive HSL color + typography tokens. Org admin sets a default for everyone; individual users can still pick their own.", tag: "Design" },
    { icon: LockKey, title: "Public forms with PIN", copy: "Publish any form to a public URL behind a captcha and a PIN gate. Anonymous submitters never need an account. Magic-link approvers don't need to log in either.", tag: "Sharing" },
    { icon: UsersThree, title: "Groups, roles, RBAC", copy: "Per-form roles (create / read / update / delete) with wildcard support. Search-as-you-type member + role pickers.", tag: "Access" },
    { icon: FilesIcon, title: "File & image uploads", copy: "5 MB per file. Direct upload to Emergent Object Storage. Inline image preview, downloadable file chips, per-org storage tracking.", tag: "Storage" },
    { icon: GitBranch, title: "Workflow history", copy: "Per-record audit trail with every step, every approver, every comment. Filter by form, status, date range. Drill into the full submitted record.", tag: "Audit" },
    { icon: Stack, title: "Import / export forms", copy: "Round-trip a form's metadata as JSON — including sections, fields, auto-generate, and nested table column config. Bootstrap from a template in minutes.", tag: "Portable" },
];

const STEPS = [
    { n: "01", title: "Define your app", copy: "Spin up a form, drop in fields and sections — or import an existing schema.", icon: FilesIcon },
    { n: "02", title: "Wire up access", copy: "Assign users, groups or roles to your form. Set per-app defaults and visibility.", icon: UsersThree },
    { n: "03", title: "Publish & route", copy: "Share an internal link, a public-with-PIN page, or kick off a multi-step approval.", icon: GitBranch },
    { n: "04", title: "Measure & iterate", copy: "Save reports as variants. Pin them to teams. Export to Excel or PDF.", icon: ChartBar },
];

export default function LandingPage() {
    const [pricing, setPricing] = useState(null);
    const [demoOpen, setDemoOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        axios.get(`${BASE}/api/public/pricing`)
            .then((r) => { if (!cancelled) setPricing(r.data); })
            .catch(() => { /* fall back to local copy */ });
        return () => { cancelled = true; };
    }, []);

    const fmtInr = (n) => `₹${Math.round(Number(n)).toLocaleString("en-IN")}`;

    return (
        <div className="min-h-screen bg-background" data-testid="landing-page">
            {/* Nav */}
            <header className="border-b border-border bg-card">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
                        <div className="font-display text-2xl font-bold tracking-tight">FormFlo<span className="text-primary">.</span></div>
                    </Link>
                    <nav className="hidden md:flex items-center gap-8 text-sm">
                        <a href="#features" className="hover:text-primary transition-colors">Features</a>
                        <a href="#how" className="hover:text-primary transition-colors">How it works</a>
                        <a href="#pricing" className="hover:text-primary transition-colors">Pricing</a>
                        <Link to="/help" className="hover:text-primary transition-colors" data-testid="nav-help">Help</Link>
                    </nav>
                    <div className="flex items-center gap-2">
                        <Link to="/login"><Button variant="ghost" className="rounded-sm" data-testid="landing-signin-btn">Sign in</Button></Link>
                        <Button variant="outline" className="rounded-sm" onClick={() => setDemoOpen(true)} data-testid="landing-demo-btn">Request a demo</Button>
                        <Link to="/signup"><Button className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="landing-start-btn">Get started <ArrowRight size={14} className="ml-1" /></Button></Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 auth-grid-bg opacity-60 pointer-events-none" aria-hidden />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
                    <div>
                        <Badge variant="outline" className="rounded-sm font-mono text-[10px] mb-6" data-testid="hero-badge">
                            <Pulse size={10} className="mr-1.5" /> Multi-tenant · low-code · production-ready
                        </Badge>
                        <h1 className="text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[0.95] mb-6" data-testid="hero-title">
                            Build internal apps<br/>
                            <span className="text-primary">without writing software.</span>
                        </h1>
                        <p className="text-lg text-muted-foreground leading-relaxed max-w-xl mb-8" data-testid="hero-sub">
                            Forms, workflows, approvals, reports, themes — orchestrated by metadata, secured by RBAC, billed annually. Ship your next operations app in an afternoon.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/signup"><Button className="rounded-sm h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="hero-start-btn">Get started <ArrowRight size={14} className="ml-2" /></Button></Link>
                            <Button variant="outline" className="rounded-sm h-12 px-6" onClick={() => setDemoOpen(true)} data-testid="hero-demo-btn">Request a demo</Button>
                        </div>
                        <div className="flex items-center gap-5 mt-10 text-xs text-muted-foreground" data-testid="hero-bullets">
                            <span className="flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-primary" /> Annual subscription</span>
                            <span className="flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-primary" /> Cancel anytime</span>
                            <span className="flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-primary" /> 7-day refund window</span>
                        </div>
                    </div>
                    <div className="relative">
                        <div className="brut-card p-8 relative z-10" data-testid="hero-card">
                            <div className="label-overline mb-2">/forms/inventory/builder</div>
                            <div className="flex items-center gap-2 mb-4">
                                <div className="text-2xl font-bold tracking-tight">Inventory request</div>
                                <Badge className="rounded-sm font-mono text-[9px] bg-success">PUBLISHED</Badge>
                            </div>
                            <div className="space-y-3">
                                {[
                                    { l: "Branch", v: "HQ Bangalore", locked: true },
                                    { l: "Item", v: "Macbook Pro 16″ M4" },
                                    { l: "Quantity", v: "1" },
                                    { l: "Justification", v: "Replacing 2022 model — battery degraded" },
                                ].map((row) => (
                                    <div key={row.l} className="grid grid-cols-[120px_1fr] gap-3 text-sm py-1.5 border-b border-border last:border-0">
                                        <div className="text-muted-foreground">{row.l}</div>
                                        <div className="font-medium flex items-center gap-2">{row.v}{row.locked && <Badge variant="outline" className="rounded-sm font-mono text-[8px]">Locked</Badge>}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
                                <div className="text-xs text-muted-foreground font-mono">Step 2 of 3 · Manager review</div>
                                <div className="flex gap-2"><div className="w-2 h-2 rounded-full bg-primary" /><div className="w-2 h-2 rounded-full bg-primary" /><div className="w-2 h-2 rounded-full bg-border" /></div>
                            </div>
                        </div>
                        <div className="absolute -top-4 -right-4 w-32 h-32 bg-accent/15 rounded-full blur-3xl" />
                        <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-primary/10 rounded-full blur-3xl" />
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="border-t border-border py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="max-w-2xl mb-12">
                        <div className="label-overline mb-2">Everything you need</div>
                        <h2 className="text-4xl font-display font-bold tracking-tight mb-3">
                            One platform. Every workflow your team runs on email today.
                        </h2>
                        <p className="text-muted-foreground">From access-control to approval chains, dashboards to PIN-protected public forms — FormFlo's metadata engine handles it.</p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="features-grid">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="brut-card p-6 group hover:border-primary/40 transition-colors" data-testid={`feature-${f.tag.toLowerCase()}`}>
                                <div className="flex items-start justify-between mb-4">
                                    <div className="inline-flex border border-border bg-secondary p-2 rounded-sm">
                                        <f.icon size={20} weight="duotone" className="text-primary" />
                                    </div>
                                    <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">{f.tag}</span>
                                </div>
                                <h3 className="text-lg font-display font-semibold mb-2">{f.title}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">{f.copy}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section id="how" className="border-t border-border py-24 bg-secondary/30">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="max-w-2xl mb-12">
                        <div className="label-overline mb-2">From idea to production</div>
                        <h2 className="text-4xl font-display font-bold tracking-tight mb-3">Four steps. About an afternoon.</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="how-it-works">
                        {STEPS.map((s) => (
                            <div key={s.n} className="brut-section p-6 relative" data-testid={`step-${s.n}`}>
                                <div className="text-5xl font-display font-bold text-primary/15 absolute top-3 right-3">{s.n}</div>
                                <s.icon size={22} weight="duotone" className="text-primary mb-4" />
                                <h3 className="font-semibold mb-1.5">{s.title}</h3>
                                <p className="text-xs text-muted-foreground leading-relaxed">{s.copy}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section id="pricing" className="border-t border-border py-24">
                <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
                    <div className="label-overline mb-2">One simple price</div>
                    <h2 className="text-4xl font-display font-bold tracking-tight mb-3">
                        {fmtInr(pricing?.monthly_price ?? 999)}
                        <span className="text-base font-normal text-muted-foreground"> / month</span>
                    </h2>
                    <p className="text-muted-foreground mb-10">Billed annually. No taxes added at checkout.</p>
                    <div className="brut-section p-10 text-left" data-testid="pricing-card">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <div className="label-overline mb-1">Standard Pack</div>
                                <div className="text-4xl font-display font-bold tabular-nums">
                                    {fmtInr(pricing?.monthly_price ?? 999)}
                                    <span className="text-base font-normal text-muted-foreground"> /mo</span>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1 font-mono">Billed annually · No taxes added</div>
                            </div>
                            <Badge className="rounded-sm font-mono text-[10px] bg-accent text-accent-foreground">ANNUAL</Badge>
                        </div>
                        <ul className="space-y-3 mb-8" data-testid="pricing-features">
                            <li className="flex items-center gap-3 text-sm"><CheckCircle size={16} weight="fill" className="text-primary" /> {pricing ? pricing.users_per_pack : 5} users per pack</li>
                            <li className="flex items-center gap-3 text-sm"><CheckCircle size={16} weight="fill" className="text-primary" /> {pricing ? pricing.forms_per_pack : 15} apps / forms per pack</li>
                            <li className="flex items-center gap-3 text-sm"><CheckCircle size={16} weight="fill" className="text-primary" /> {pricing ? pricing.records_per_pack.toLocaleString("en-IN") : "10,000"} records per pack</li>
                            <li className="flex items-center gap-3 text-sm"><CheckCircle size={16} weight="fill" className="text-primary" /> {pricing ? pricing.storage_gb_per_pack : 1} GB storage per pack</li>
                            <li className="flex items-center gap-3 text-sm"><CheckCircle size={16} weight="fill" className="text-primary" /> Unlimited workflows, reports & themes</li>
                            <li className="flex items-center gap-3 text-sm"><CheckCircle size={16} weight="fill" className="text-primary" /> Public links with PIN + captcha</li>
                            <li className="flex items-center gap-3 text-sm"><CheckCircle size={16} weight="fill" className="text-primary" /> Excel & PDF exports</li>
                        </ul>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <Link to="/signup">
                                <Button className="w-full rounded-sm h-12 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="pricing-cta-btn">
                                    Get started <ArrowRight size={14} className="ml-2" />
                                </Button>
                            </Link>
                            <Button variant="outline" className="w-full rounded-sm h-12" onClick={() => setDemoOpen(true)} data-testid="pricing-demo-btn">
                                Request a demo
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-border py-10 bg-card" data-testid="landing-footer">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col gap-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="font-display text-lg font-bold tracking-tight">FormFlo<span className="text-primary">.</span></div>
                            <span className="hidden sm:inline text-xs text-muted-foreground">·</span>
                            <div className="text-xs text-muted-foreground">
                                A solution from{" "}
                                <a
                                    href="https://rishmathi.com"
                                    target="_blank" rel="noopener noreferrer"
                                    className="text-primary hover:underline font-medium"
                                    data-testid="footer-rishmathi-link"
                                >
                                    Rishmathi Tech Ventures Pvt Ltd
                                </a>
                            </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <Link to="/login" className="hover:text-primary">Sign in</Link>
                            <Link to="/signup" className="hover:text-primary">Get started</Link>
                        </div>
                    </div>
                    <div className="border-t border-border pt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-muted-foreground">
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                            <Link to="/privacy" className="hover:text-primary" data-testid="footer-privacy-link">Privacy Policy</Link>
                            <Link to="/terms" className="hover:text-primary" data-testid="footer-terms-link">Terms &amp; Conditions</Link>
                            <Link to="/refund" className="hover:text-primary" data-testid="footer-refund-link">Refund Policy</Link>
                            <a href="mailto:contact@rishmathi.com" className="hover:text-primary" data-testid="footer-contact-link">contact@rishmathi.com</a>
                        </div>
                        <div>© {new Date().getFullYear()} Rishmathi Tech Ventures Pvt Ltd · Hyderabad, India</div>
                    </div>
                </div>
            </footer>

            <RequestDemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
        </div>
    );
}
